import { type FC } from 'react';
import { useFragment, graphql } from 'react-relay';

import { RegularTileContent } from './RegularTileContent';
import { CreatorContent } from './CreatorContent';
import { BurnedItemTileContent } from './BurnedItemTileContent';
import { CollectorTileContent } from './CollectorTileContent';

import { NftItemTileType } from '../../helpers/nftItemTileType';

import { type NftItemTileContent_viewer$key } from './__generated__/NftItemTileContent_viewer.graphql';
import { type NftItemTileContent_nftItem$key } from './__generated__/NftItemTileContent_nftItem.graphql';
import { type NftItemTileContent_nftAuction$key } from './__generated__/NftItemTileContent_nftAuction.graphql';

type Props = {
    tileType: keyof typeof NftItemTileType;
    viewer: NftItemTileContent_viewer$key;
    nftItem: NftItemTileContent_nftItem$key;
    nftAuction?: NftItemTileContent_nftAuction$key | null | undefined;
    showCreator?: boolean;
    currency: string;
    isMobile: boolean;
};

const {
    REGULAR_TILE,
    CREATOR_REGULAR,
    COLLECTOR_TILE,
    COLLECTOR_TILE_NO_CTA,
    COLLECTOR_TILE_NO_CREATOR,
} = NftItemTileType;

const viewerFragment = graphql`
    fragment NftItemTileContent_viewer on Viewer {
        ...CollectorTileContent_viewer
    }
`;

const nftItemFragment = graphql`
    fragment NftItemTileContent_nftItem on NftItem {
        serviceId
        wasBurned
        ...RegularTileContent_nftItem
        ...CreatorContent_nftItem
        ...BurnedItemTileContent_nftItem
        ...CollectorTileContent_nftItem
    }
`;

const nftAuctionFragment = graphql`
    fragment NftItemTileContent_nftAuction on NftAuction {
        ...RegularTileContent_nftAuction
        ...CreatorContent_nftAuction
        ...CollectorTileContent_nftAuction
    }
`;

const NftItemTileContent: FC<Props> = props => {
    const {
        tileType,
        nftItem: nftItemRef,
        nftAuction: nftAuctionRef = null,
        viewer: viewerRef,
    } = props;

    const viewer = useFragment(viewerFragment, viewerRef);
    const nftItem = useFragment(nftItemFragment, nftItemRef);
    const nftAuction = useFragment(nftAuctionFragment, nftAuctionRef);

    if (nftItem.wasBurned) {
        return <BurnedItemTileContent nftItem={nftItem} creatorDisplay="link" />;
    }

    return (
        <>
            {tileType === REGULAR_TILE && (
                <RegularTileContent {...props} nftItem={nftItem} nftAuction={nftAuction} />
            )}
            {tileType === CREATOR_REGULAR && (
                <CreatorContent {...props} nftItem={nftItem} nftAuction={nftAuction} />
            )}

            {tileType.startsWith(COLLECTOR_TILE) && (
                <CollectorTileContent
                    {...props}
                    viewer={viewer}
                    nftItem={nftItem}
                    nftAuction={nftAuction}
                    showAuction="ifCreated"
                    showCtas={tileType !== COLLECTOR_TILE_NO_CTA}
                    showCreator={tileType !== COLLECTOR_TILE_NO_CREATOR}
                    nftItemId={nftItem?.serviceId}
                />
            )}
        </>
    );
};

export { NftItemTileContent };
